import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import DataImportView from 'FeatureModules/DataImport/Components/DataImportView';
import Loader from 'Components/Forms/Loader';
import usePageTitle from 'hooks/usePageTitle';
import { ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import React, { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useMeasure from 'react-use/lib/useMeasure';
import { ThemeButton } from 'DesignSystem';
import useDataImportOnboarding from 'FeatureModules/DataImport/hooks/useDataImportOnboarding';
import { Tour } from '@styled-icons/material/Tour';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import FlexBox from 'Components/FlexBox';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

const Container = styled('div')(({ theme }) => ({
  position: 'relative',
  minHeight: 50,
  padding: theme.spacing(0.5),
}));

const DataImportOverview: FC = () => {
  usePageTitle('Farms import');

  const navigate = useNavigate();
  const { company, plan } = useCompanyPlanContext();
  const isPartner = plan === ECustomerPlan.FREEMIUM;
  const { id, importId } = useParams<{ id?: string; importId?: string }>();
  const [containerRef, { height: containerHeight }] = useMeasure<HTMLDivElement>();
  const { displayOnboardingTour, handleStartOnboardingTour } = useDataImportOnboarding({
    // only display onboarding tour in edit mode
    hideTour: !!importId,
  });

  // Do the import for the partner if the id is present, otherwise do it for the your own company
  const importForPartnerId = id ? id : company.id;

  const handleDataImportCreated = () => {
    navigate(!isPartner ? `/partners/${importForPartnerId}/imports` : '/imports');
  };

  if (!importForPartnerId) {
    return <Loader />;
  }

  return (
    <>
      <PageContainer ref={containerRef}>
        <Box display="flex" alignItems="end">
          <Box width="100%">
            <PageTitle
              title={'Origin data import'}
              goBackLabel="All data sets"
              goBackUrl={id ? `/partners/${id}/imports?tab=1` : '/imports?tab=1'}
              enableAutoGoBackHistoryButton
              TitleAdornment={
                <FlexBox ml={1} mt={0.5}>
                  <Icon name="farm" color="gray-60" size="large" />
                  <Box mr={0.5} />
                  <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_60">
                    Farm data
                  </ThemeTypography>
                </FlexBox>
              }
            />
          </Box>
          {!importId && (
            <Box pr={1}>
              <ThemeButton
                color="WHITE"
                data-testid="restart-tour-button"
                size="small"
                startIcon={<Tour size={16} color="black" />}
                onClick={handleStartOnboardingTour}
              >
                Restart guided tour
              </ThemeButton>
            </Box>
          )}
        </Box>

        <Container>
          {!!containerHeight ? (
            <DataImportView
              containerHeight={containerHeight}
              datasetId={importId}
              displayOnboardingTour={displayOnboardingTour}
              companyId={importForPartnerId}
              onDataImportCreated={handleDataImportCreated}
            />
          ) : (
            <Loader />
          )}
        </Container>
      </PageContainer>
    </>
  );
};

export default DataImportOverview;
